import type { ReportFreshness } from "@esi/shared";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { cn } from "@/lib/utils";
import { formatDateTime } from "../../i18n/format.ts";

/**
 * When the figures were computed, and a plain warning once they are older than two hours. Above
 * them, "Includes synthetic demo data" when any figure of the period is synthetic
 * (demo-world-v2): in words, never by colour alone.
 */
export function Freshness(props: { freshness: ReportFreshness; synthetic?: boolean | undefined }) {
  const { t } = useTranslation();
  const { freshness } = props;
  const at = freshness.computedAt === null ? null : formatDateTime(freshness.computedAt);
  return (
    <>
      {props.synthetic ? <SyntheticNotice /> : null}
      {at === null ? (
        <Notice tone="warning">{t("reports.neverComputed")}</Notice>
      ) : freshness.stale ? (
        <Notice tone="warning">{t("reports.stale", { at })}</Notice>
      ) : (
        <p className="text-xs text-fg-muted">{t("reports.updated", { at })}</p>
      )}
    </>
  );
}

/** The report's figures include synthetic demo data (spec `usage-reporting`). */
export function SyntheticNotice() {
  const { t } = useTranslation();
  return (
    <Notice tone="info" title={t("reports.synthetic")}>
      {t("reports.syntheticBody")}
    </Notice>
  );
}

/** A titled block of a report. */
export function Section(props: { title: string; children: ReactNode; aside?: ReactNode }) {
  const id = `report-${props.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={id} className="text-sm font-semibold text-fg">
          {props.title}
        </h2>
        {props.aside}
      </div>
      {props.children}
    </section>
  );
}

/**
 * The scroll container's background: soft shadows at the edges that still have columns beyond
 * them (the `local` layers cover the shadow once that edge is reached), so a table wider than
 * the screen shows it scrolls.
 */
export const SCROLL_CUE = {
  background: [
    "linear-gradient(to right, var(--surface-raised) 30%, transparent) left center / 2.5rem 100% no-repeat local",
    "linear-gradient(to left, var(--surface-raised) 30%, transparent) right center / 2.5rem 100% no-repeat local",
    "linear-gradient(to right, color-mix(in oklch, var(--text-primary) 14%, transparent), transparent) left center / 0.75rem 100% no-repeat scroll",
    "linear-gradient(to left, color-mix(in oklch, var(--text-primary) 14%, transparent), transparent) right center / 0.75rem 100% no-repeat scroll",
    "var(--surface-raised)",
  ].join(", "),
};

/**
 * A plain figures table: the first column names the row, the others are numbers aligned to the
 * end unless `align` says otherwise. Figures never wrap: a table wider than the screen scrolls
 * sideways, with an edge cue. `compact` suits a wide table: tighter cells, headers that wrap when
 * the columns would not fit otherwise, and a first column that stays in view as it scrolls.
 */
export function FigureTable(props: {
  caption: string;
  columns: readonly string[];
  rows: readonly { key: string; cells: readonly ReactNode[] }[];
  /** Per column after the first: `start` for text (an owner), `end` (default) for figures. */
  align?: readonly ("start" | "end")[];
  compact?: boolean;
}) {
  const end = (i: number) => (props.align?.[i - 1] ?? "end") === "end";
  const pad = props.compact ? "px-2 py-2 first:pl-3 last:pr-3" : "px-4 py-2";
  // A compact table's row header stays put while the figures scroll under it (opaque, so they
  // never show through), once the table is wide enough that pinning it leaves room for figures.
  const sticky = props.compact && "sticky-edge @sm:sticky left-0 z-10 bg-surface-raised";
  return (
    <div
      // A compact table's cells size themselves to the room it has (`@sm:`, `@5xl:` and the like).
      className={cn("overflow-x-auto rounded-lg border border-line", props.compact && "@container")}
      style={SCROLL_CUE}
    >
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">{props.caption}</caption>
        <thead className="border-b border-line text-xs text-fg-muted">
          <tr>
            {props.columns.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={cn(
                  pad,
                  "font-medium",
                  props.compact ? "align-bottom text-balance" : "whitespace-nowrap",
                  i === 0 && sticky,
                  i > 0 && end(i) && "text-right",
                )}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {props.rows.map((row) => (
            <tr key={row.key}>
              {row.cells.map((cell, i) =>
                i === 0 ? (
                  <th
                    key={0}
                    scope="row"
                    className={cn(pad, sticky, "min-w-32 font-normal text-fg")}
                  >
                    {cell}
                  </th>
                ) : (
                  <td
                    // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional.
                    key={i}
                    className={cn(
                      pad,
                      "whitespace-nowrap text-fg-secondary",
                      end(i) && "text-right tabular-nums",
                    )}
                  >
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A project's name as reports show it. Personal projects are never listed one by one: they come
 * as one row without ID or name, all personal projects together.
 */
export function useProjectName() {
  const { t } = useTranslation();
  // A standalone line names no conversation: "Standalone conversations" of its entity.
  return (row: { name: string | null; standalone?: boolean | undefined }) =>
    row.standalone ? t("reports.standaloneLine") : (row.name ?? t("reports.personalProject"));
}
